import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useDuplicateStratigraphy } from '@/components/configurator-v2/hooks/useDuplicateStratigraphy';
import { toast } from 'sonner';

/**
 * Prepara la modifica di una parete PARTENDO DA UNA RIGA DI PREVENTIVO.
 *
 * Perche' esiste. Il pulsante che apriva il configuratore da una riga passava
 * l'id della parete di CATALOGO con `viewOnly=true` — un parametro che nessuno
 * legge piu' da quando il configuratore V1 e' stato dismesso. Risultato: quella
 * che si chiamava "visualizza" era una modifica in piena regola sulla parete
 * condivisa. Fra le pareti usate nei preventivi ce ne sono quattro certificate
 * Saint-Gobain del catalogo globale, presenti in 30 preventivi ciascuna: bastava
 * un salvataggio per cambiarle a tutte le aziende della piattaforma, lasciandole
 * per giunta marcate come certificate.
 *
 * Ora la modifica avviene sempre su una copia che appartiene all'organizzazione
 * e serve solo a quel preventivo. Il catalogo condiviso non e' piu' raggiungibile
 * in scrittura da qui — per costruzione, non per avviso.
 */

export interface PrepareEditInput {
  /** Riga di preventivo (`estimate_stratigraphies.id`). */
  estimateStratigraphyId: string;
  estimateId: string;
  /** Parete attualmente collegata alla riga. */
  stratigraphyId: string;
}

export interface PrepareEditResult {
  /** Parete da aprire nel configuratore: la copia, o l'originale se era gia' nostra. */
  stratigraphyId: string;
  /** `true` se e' stata creata una copia adesso. */
  copiaCreata: boolean;
  /** Quanti ALTRI preventivi usavano la parete di partenza. */
  altriPreventivi: number;
}

/**
 * Si puo' modificare sul posto? Solo se la parete e' dell'organizzazione, non
 * certificata, e non la usa nessun altro preventivo. In tutti gli altri casi si
 * lavora su una copia.
 */
async function serveUnaCopia(
  stratigraphyId: string,
  estimateStratigraphyId: string,
  currentOrganizationId: string,
): Promise<{ serve: boolean; altriPreventivi: number; nomeOriginale: string }> {
  const { data: strat, error } = await supabase
    .from('stratigraphies')
    .select('id, name, organization_id, is_certified')
    .eq('id', stratigraphyId)
    .maybeSingle();
  if (error) throw error;
  if (!strat) throw new Error('Parete non trovata nel catalogo');

  // Quante ALTRE righe di preventivo puntano a questa parete.
  const { data: usi, error: usiErr } = await supabase
    .from('estimate_stratigraphies')
    .select('id, estimate_id')
    .or(`stratigraphy_id.eq.${stratigraphyId},original_stratigraphy_id.eq.${stratigraphyId}`);
  if (usiErr) throw usiErr;

  const altre = (usi ?? []).filter(u => u.id !== estimateStratigraphyId);
  const altriPreventivi = new Set(altre.map(u => u.estimate_id)).size;

  const nostra = strat.organization_id === currentOrganizationId;
  const serve = !nostra || !!strat.is_certified || altre.length > 0;

  return { serve, altriPreventivi, nomeOriginale: strat.name ?? 'Parete' };
}

export function useEditStratigraphyForEstimate() {
  const qc = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();
  const { duplicateStratigraphy } = useDuplicateStratigraphy();

  const mutation = useMutation({
    mutationFn: async (input: PrepareEditInput): Promise<PrepareEditResult> => {
      const { estimateStratigraphyId, estimateId, stratigraphyId } = input;
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');

      const { serve, altriPreventivi, nomeOriginale } = await serveUnaCopia(
        stratigraphyId, estimateStratigraphyId, currentOrganizationId,
      );

      // Gia' nostra e usata solo qui: si modifica direttamente, senza generare
      // una copia nuova a ogni apertura.
      if (!serve) {
        return { stratigraphyId, copiaCreata: false, altriPreventivi: 0 };
      }

      // Il numero di offerta nel nome rende riconoscibile da dove viene la copia.
      const { data: estimate } = await supabase
        .from('estimates')
        .select('offer_number')
        .eq('id', estimateId)
        .maybeSingle();
      const riferimento = estimate?.offer_number ?? 'preventivo';

      const { id: copiaId } = await duplicateStratigraphy({
        sourceId: stratigraphyId,
        name: `${nomeOriginale} · ${riferimento}`,
        silent: true,
      });

      // La riga punta alla copia, e il suo snapshot viene rigenerato da li'.
      // Stessa scrittura del "ricollega al catalogo", con un id scelto invece
      // che suggerito. NON si toccano `unit_cost`/`total_cost`: il prezzo si
      // riallinea dopo il salvataggio nel configuratore, e solo sulle bozze.
      const { data: fresh, error: freshErr } = await supabase
        .from('stratigraphies')
        .select('*, layers(*, materials!layers_material_id_fkey(*), screw_materials:materials!layers_screw_material_id_fkey(*))')
        .eq('id', copiaId)
        .single();
      if (freshErr || !fresh) throw new Error('Copia creata ma non rileggibile');

      const { error: updErr } = await supabase
        .from('estimate_stratigraphies')
        .update({
          stratigraphy_id: copiaId,
          original_stratigraphy_id: copiaId,
          stratigraphy_data: fresh,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          layers_data: ((fresh as any).layers ?? []).slice().sort((a: any, b: any) => a.position - b.position),
        })
        .eq('id', estimateStratigraphyId);
      if (updErr) throw updErr;

      return { stratigraphyId: copiaId, copiaCreata: true, altriPreventivi };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['estimate-stratigraphies'] });
      qc.invalidateQueries({ queryKey: ['estimate'] });
    },
    onError: (err: Error) => {
      console.error('[useEditStratigraphyForEstimate]', err);
      toast.error(err.message || 'Impossibile preparare la modifica');
    },
  });

  return {
    prepareEdit: mutation.mutateAsync,
    isPreparing: mutation.isPending,
  };
}
